<template>
	<CreationWizardStepWrapper>
		<template #header>
			<ChoosePlacementForAddOnHeader v-bind="$props" v-on="$listeners" />
		</template>
		<div class="display-flex height-100 flex-wrap">
			<div class="flex-col-12">
				<div class="top-section">
					<h2 class="med">Choose an <span class="bold">element</span> and a <span class="bold">context</span></h2>
					<h3 class="reg">
						Elements and contexts determine where your feature appears and what data and actions are available to you.
						<a href="#" target="_blank">learn more</a>
					</h3>
				</div>
				<PlacementSelector
					class="list-selector mt-3"
					:selectedPlacementId="selectedPlacementId"
					:selectedPlacement="selectedPlacement"
					:placements="enrichedPlacements"
					:contextsOfPlacements="contexts"
					:generalPlacements="generalPlacements"
					:selectedElementType="selectedElementType || initialElementType"
					@selectPlacement="selectPlacement"
					:serviceName="service.service_name"
					:tilesAreActive="tilesAreActive"
					:useSingletonPlacements="useSingletonPlacements"
				/>
				<ConditionalRenderingFieldsFormDataProvider
					v-if="showFieldsForm"
					class="pt-6"
					:fields="conditionalRenderingFields"
					:contextActionId="contextActionId"
					:contextServiceId="selectedAppId"
					:credentialsConfiguration="selectedCredentialsConfiguration"
					:context="selectedContext"
					:paramsMap.sync="paramsMap"
					:paramsMapLabels.sync="paramsMapLabels"
					:addOnBaseUrl="currentUrl"
					@all-fields-available="setAllFieldsAvailable"
				/>
				<div v-if="hasDisabledPlacementOptions" class="pt-2 enable-options-text">To enable all options, go back and connect account</div>
			</div>
			<div class="flex-1 flex-center--end pt-6" v-if="showContinueButton">
				<PrimaryButton @click="nextStep" :disabled="!allFieldsAreFilled">Continue</PrimaryButton>
			</div>
		</div>
	</CreationWizardStepWrapper>
</template>

<script>
import props from './ChoosePlacementForAddOn.props';
import ChoosePlacementForAddOnHeader from './ChoosePlacementForAddOnHeader.vue';
import ConditionalRenderingFieldsFormDataProvider from './ConditionalRenderingFieldsFormDataProvider.vue';
import PlacementSelector from '../common/PlacementSelector.vue';
import WizardStepMixin from '../WizardStepMixin';
import AppService from '@/utils/AppService';
import ContextService from '@/utils/ContextService';
import { isNullOrUndefinedOrEmpty } from '@/utils/helpers';
export default {
	name: 'ChoosePlacementForAddOn',
	mixins: [WizardStepMixin],
	components: { ChoosePlacementForAddOnHeader, PlacementSelector, ConditionalRenderingFieldsFormDataProvider },
	props,
	computed: {
		enrichedPlacements() {
			return this.placements.map((x) => ({
				...x,
				disabled: this.skippedAuth && !x.version.skip_auth,
			}));
		},
		selectedPlacement: {
			get() {
				return this.findPlacement(this.selectedPlacementId);
			},
			set(placementId) {
				this.updateState({ key: 'selectedPlacementId', value: placementId });
			},
		},
		_selectedElementType: {
			get() {
				return this.selectedElementType;
			},
			set(elementType) {
				this.updateState({ key: 'selectedElementType', value: elementType });
			},
		},
		hasDisabledPlacementOptions() {
			return this.enrichedPlacements.some((x) => x.disabled);
		},
		skippedAuth() {
			return !this.selectedCredentialsId && AppService.doesRequiresAuth(this.service);
		},
		selectedCredentialsId() {
			return this.selectedCredentialsConfiguration.id;
		},
		hasConditionalRenderingFields() {
			return this.selectedContext ? ContextService.hasConditionalRenderingFields(this.selectedContext) : false;
		},
		conditionalRenderingFields() {
			return this.selectedContext ? ContextService.getConditionalRenderingFields(this.selectedContext) : [];
		},
		showFieldsForm() {
			return this.hasConditionalRenderingFields && !!this.contextActionId;
		},
		contextActionId() {
			return this.selectedContext?.action;
		},
		showContinueButton() {
			return !!this.selectedPlacementId;
		},
		selectedContext() {
			return this.selectedPlacement?.context; // context was enriched in ChoosePlacementForAddOnDataProvider
		},
		paramsMap: {
			get() {
				return this.selectedPlacementParamsMap;
			},
			set(value) {
				this.updateState({ key: 'selectedPlacementParamsMap', value });
			},
		},
		paramsMapLabels: {
			get() {
				return this.selectedPlacementParamsMapLabels;
			},
			set(value) {
				this.updateState({ key: 'selectedPlacementParamsMapLabels', value });
			},
		},
		allFieldsAreFilled() {
			return this.conditionalRenderingFields.every(
				(fieldKey) => !isNullOrUndefinedOrEmpty(this.paramsMap[fieldKey]) && !isNullOrUndefinedOrEmpty(this.paramsMapLabels[fieldKey])
			);
		},
	},
	methods: {
		findPlacement(placementId) {
			return [...this.placements, ...this.generalPlacements].find((placement) => placement.id === placementId);
		},
		selectPlacement({ placementId, elementType }) {
			this.selectedPlacement = placementId;
			this.$nextTick(() => {
				const placementIsSelected = !!this.selectedPlacement;
				this._selectedElementType = elementType;
				let selectedPlacementVersionId = null;
				let selectedContextId = null;
				if (placementIsSelected) {
					selectedPlacementVersionId = this.selectedPlacement.version.version_id;
					if (this.selectedPlacement.context) {
						selectedContextId = this.selectedPlacement.context.id;
					}
				}

				this.updateState({ key: 'selectedPlacementVersionId', value: selectedPlacementVersionId });
				this.updateState({ key: 'selectedContextId', value: selectedContextId });
				if (placementIsSelected && !this.hasConditionalRenderingFields) {
					this.nextStep();
				}
			});
		},
		setAllFieldsAvailable(allFieldsAvailable) {
			if (!this.tickAfterMount) {
				return;
			}
			// if not allFieldsAvailable, then we're not waiting for user to fill ConditionalRenderingFieldsForm
			if (!allFieldsAvailable) {
				this.nextStep();
			}
		},
	},
	data() {
		return { tickAfterMount: false };
	},
	mounted() {
		this.$nextTick(() => {
			this.tickAfterMount = true;
		});
	},
};
</script>

<style lang="scss" scoped>
@import '@/components/CreationWizard/assets/creation-wizard-vars.scss';
.enable-options-text {
	font-size: 12px;
}
.top-section {
	h3 {
		color: $secondary-content-title-font-color;
		a {
			color: $secondary-content-title-font-color;
		}
	}
}
</style>
